import hljs from "highlight.js/lib/core";
import cLang from "highlight.js/lib/languages/c";
import cppLang from "highlight.js/lib/languages/cpp";
import javascriptLang from "highlight.js/lib/languages/javascript";

hljs.registerLanguage("c", cLang);
hljs.registerLanguage("cpp", cppLang);
// `RV-15`: the JavaScript track's blocks were the only ones left plain.
hljs.registerLanguage("javascript", javascriptLang);

const CODE_LANG_LABELS: Record<string, string> = {
  c: "C",
  cpp: "C++",
  python: "Python",
  java: "Java",
  javascript: "JavaScript",
  typescript: "TypeScript",
  bash: "Shell",
  shell: "Shell",
};

interface EnhanceOptions {
  /** Wrap each block in a header with a language badge + copy button. */
  chrome?: boolean;
  /**
   * `LN-UI05`: a Run button beside Copy on the blocks the caller says can run.
   * The button carries no handler of its own: the Learn reader listens for
   * clicks on `button.pc-code-run` and sends the block's code to its editor,
   * and the course player passes nothing and gets no button.
   */
  run?: {
    label: string;
    canRun: (lang: string | undefined, code: string) => boolean;
  };
  /**
   * `RV-15`: the Copy button's words in the site's language. The DOM is built
   * here, outside React, so the caller passes them in; without them the button
   * reads in English as it always did.
   */
  labels?: { copy: string; copied: string; copyCode: string };
}

const ENGLISH_LABELS = { copy: "Copy", copied: "Copied", copyCode: "Copy code" };

/**
 * Sanitized lesson HTML cannot carry <button> or highlight spans, so code-block
 * chrome (language badge + copy) and hljs token spans are injected client-side
 * after render. Idempotent — guarded by data-pc-enhanced.
 */
export function enhanceCodeBlocks(root: HTMLElement, options: EnhanceOptions = {}) {
  const { chrome = true, run, labels = ENGLISH_LABELS } = options;

  root.querySelectorAll<HTMLPreElement>("pre").forEach((pre) => {
    if (pre.dataset.pcEnhanced === "true") return;
    pre.dataset.pcEnhanced = "true";

    const code = pre.querySelector("code");
    const langMatch = code?.className.match(/language-([\w+#-]+)/);
    const lang = langMatch?.[1]?.toLowerCase();
    if (code && lang && hljs.getLanguage(lang)) {
      hljs.highlightElement(code);
    }

    if (!chrome) return;

    // Skip chrome for output-style blocks and Mermaid diagram sources —
    // they get their own styling (and the diagram pre is hidden after render).
    // A widget's own code line is part of its frame (`LN-WGT02`): wrapped as a
    // code block it turned into an empty dark panel inside the widget.
    if (pre.closest(".pc-output") || pre.closest('[data-type="diagram"]') || pre.closest("[data-widget]")) return;

    const header = document.createElement("div");
    header.className = "pc-code-head";

    const label = document.createElement("span");
    label.className = "pc-code-lang";
    label.textContent = lang ? (CODE_LANG_LABELS[lang] ?? lang.toUpperCase()) : "Code";

    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "pc-code-copy";
    copyBtn.textContent = labels.copy;
    copyBtn.setAttribute("aria-label", labels.copyCode);
    copyBtn.addEventListener("click", () => {
      const text = code?.textContent ?? pre.textContent ?? "";
      navigator.clipboard
        ?.writeText(text)
        .then(() => {
          copyBtn.textContent = `${labels.copied} ✓`;
          copyBtn.classList.add("is-copied");
          window.setTimeout(() => {
            copyBtn.textContent = labels.copy;
            copyBtn.classList.remove("is-copied");
          }, 1600);
        })
        .catch(() => {});
    });

    const actions = document.createElement("span");
    actions.className = "pc-code-actions";
    actions.append(copyBtn);
    if (run?.canRun(lang, code?.textContent ?? pre.textContent ?? "")) {
      const runBtn = document.createElement("button");
      runBtn.type = "button";
      runBtn.className = "pc-code-run";
      runBtn.textContent = run.label;
      actions.append(runBtn);
    }

    header.append(label, actions);

    const wrapper = document.createElement("div");
    wrapper.className = "pc-codeblock";
    pre.parentNode?.insertBefore(wrapper, pre);
    wrapper.append(header, pre);
  });
}
